import { Box, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { IconCheckCircleFilled, IconDot, IconStarFilled, IconStarOutline } from "@/assets/icons";
import { mastery } from "@/theme/colors";
import { typography } from "@/theme/typography";
import {
  RECENT_RESULTS_SLOT_COUNT,
  getMasteryLevel,
  isMasteredByCorrectCount,
  TVocabularyAnswerResult,
  TVocabularyMasteryLevel,
} from "@/infrastructure/adapters/vocabulary";

/**
 * Label + color per mastery level.
 */
const LEVEL_DISPLAY: Record<TVocabularyMasteryLevel, { label: string; color: string }> = {
  learning: { label: "習得中", color: mastery.learning },
  strong: { label: "得意", color: mastery.strong },
  strongish: { label: "やや得意", color: mastery.strongish },
  weakish: { label: "やや苦手", color: mastery.weakish },
  weak: { label: "苦手", color: mastery.weak },
};

type TVocabularyMasteryIndicatorProps = {
  /** Results of the last 3 attempts, oldest first */
  recentResults: TVocabularyAnswerResult[];
  /** Cumulative correct-answer count; >=2 shows the permanent ✓ badge */
  masteryCorrectCount: number;
};

/**
 * Mastery status shown above a vocabulary word: a level label plus one marker per
 * attempt slot — ★ correct, ☆ wrong, ・ not attempted yet — and a ✓ once mastered.
 */
export const VocabularyMasteryIndicator: FC<TVocabularyMasteryIndicatorProps> = ({ recentResults, masteryCorrectCount }) => {
  const level = useMemo(() => getMasteryLevel(recentResults), [recentResults]);
  const isMastered = isMasteredByCorrectCount(masteryCorrectCount);

  return (
    <Stack alignItems="center">
      <Typography sx={{ ...typography.sub16Bold, color: LEVEL_DISPLAY[level].color }}>{LEVEL_DISPLAY[level].label}</Typography>

      <Stack direction="row" alignItems="center" sx={{ position: "relative", color: mastery.star, lineHeight: 1 }}>
        {Array.from({ length: RECENT_RESULTS_SLOT_COUNT }, (_, index) => {
          const result = recentResults[index];

          return (
            <Stack key={index} alignItems="center" justifyContent="center" sx={{ width: "20px", fontSize: "20px" }}>
              {result ? result === "correct" ? <IconStarFilled /> : <IconStarOutline /> : <IconDot />}
            </Stack>
          );
        })}

        {isMastered && (
          <Box sx={{ position: "absolute", left: "100%", top: "50%", transform: "translateY(-50%)", pl: 0.5, lineHeight: 0 }}>
            <IconCheckCircleFilled style={{ fontSize: "20px", color: mastery.mastered }} />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
